import React, { useEffect, useRef, useState } from "react";
import { Container } from "react-bootstrap";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Carousel } from "react-responsive-carousel";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import ProjectItem from "../projects/ProjectItem";
import "./Projects.css";

gsap.registerPlugin(ScrollTrigger);

function CurrentProjects() {
  const sectionRef = useRef(null);
  const headerRef = useRef(null);
  const carouselRef = useRef(null);
  const stackRef = useRef(null);
  const panelsRef = useRef([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== "undefined" ? window.innerWidth >= 768 : true
  );

  const projects = [
    {
      id: 1,
      title: "Roselight Care App",
      description:
        "A healthcare management platform for Roselight Healthcare Services that helps care managers oversee client wellbeing at a glance — tracking care plans, medication schedules, handovers, and appointments in one place.",
      image: "images/projects/p1.jpg",
      category: "HealthTech",
      tech: ["React", "Node.js", "Charts"],
      year: "2024",
    },
    {
      id: 2,
      title: "YAEI Connect",
      description:
        "The Young African Entrepreneurs Institute's programme management platform for coordinating masterclasses, events, and business maturity tracking across their cohorts of young entrepreneurs.",
      image: "images/projects/p2.png",
      category: "EdTech",
      tech: ["React", "Calendar", "Analytics"],
      year: "2024",
    },
    {
      id: 3,
      title: "IMAT TECH",
      description:
        "A fleet operations dashboard for a logistics company — live truck tracking, delivery proofs, tonnage-per-product reporting, and daily loading logs, giving dispatch a complete picture of what's on the road.",
      image: "images/projects/p3.jpg",
      category: "Logistics",
      tech: ["React", "Mapbox", "Real-time"],
      year: "2025",
    },
    {
      id: 4,
      title: "Dropwise Hydrotech",
      description:
        "A smart water monitoring solution built with Team One More Thing — tracking household consumption, tank and geyser levels, garden soil conditions, and water quality metrics from a single dashboard.",
      image: "images/projects/p4.jpg",
      category: "IoT",
      tech: ["IoT", "Sensors", "Dashboard"],
      year: "2025",
    },
    {
      id: 5,
      title: "WCCO Portal",
      description:
        "The Wits Citizenship and Community Outreach portal — helping students discover volunteering projects, log participation hours, and giving faculty oversight of engagement across the university.",
      image: "images/projects/p5.jpg",
      category: "EdTech",
      tech: ["React", "Auth", "Reporting"],
      year: "2023",
    },
    {
      id: 6,
      title: "SACAQM AI_R",
      description:
        "An air quality monitoring platform for the South African Centre for Air Quality Monitoring — surfacing live PM1.0–10, NOX, VOC, temperature and humidity readings from sensor stations across Johannesburg.",
      image: "images/projects/p6.JPG",
      category: "Environmental",
      tech: ["IoT", "Mapbox", "Time-series"],
      year: "2023",
    },
    {
      id: 7,
      title: "SETMind",
      description:
        "A tutoring operations platform for managing tutors, students, and invoice approvals across universities — streamlining the finance flow between institutions, tutors, and the coordinating team.",
      image: "images/projects/p7.JPG",
      category: "EdTech",
      tech: ["React", "Workflow", "Payments"],
      year: "2025",
    },
    {
      id: 8,
      title: "FlexiHub",
      description:
        "A FinTech platform for South African SMEs bundling accounting, funding, insurance, and compliance in one suite — with Zenzele, an embedded AI agent that helps founders navigate financial decisions.",
      image: "images/projects/p8.png",
      category: "FinTech",
      tech: ["React", "AI Agent", "Accounting"],
      year: "2025",
    },
    {
      id: 9,
      title: "IIMS",
      description:
        "The Impact Investment Management System — an evolution of YAEI Connect — giving organisations a provincial view of entrepreneur impact, jobs created, and investment readiness across South Africa.",
      image: "images/projects/p9.png",
      category: "Impact",
      tech: ["React", "Leaflet", "Analytics"],
      year: "2026",
    },
    {
      id: 10,
      title: "IMSA Voting System",
      description:
        "The International Miss South Africa voting platform — letting supporters cast paid votes for contestants, with real-time leaderboards, event check-ins, and a full payment audit trail per voter.",
      image: "images/projects/p10.png",
      category: "Events",
      tech: ["React", "Payments", "Real-time"],
      year: "2026",
    },
  ];

  // Track desktop vs mobile
  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= 768);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Header animation (always)
  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        headerRef.current,
        { y: 80, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 1.2,
          ease: "power3.out",
          scrollTrigger: {
            trigger: headerRef.current,
            start: "top 85%",
            toggleActions: "play none none reverse",
          },
        }
      );
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  // Carousel entrance (mobile only)
  useEffect(() => {
    if (isDesktop) return;
    const ctx = gsap.context(() => {
      gsap.fromTo(
        carouselRef.current,
        { y: 100, opacity: 0, scale: 0.95 },
        {
          y: 0,
          opacity: 1,
          scale: 1,
          duration: 1,
          ease: "back.out(1.5)",
          scrollTrigger: {
            trigger: carouselRef.current,
            start: "top 80%",
            toggleActions: "play none none reverse",
          },
        }
      );
    }, sectionRef);
    return () => ctx.revert();
  }, [isDesktop]);

  // Stacking scroll (desktop only) — pinned with ScrollTrigger (sticky is
  // unavailable because an ancestor uses overflow: hidden)
  useEffect(() => {
    if (!isDesktop) return;
    const panels = panelsRef.current.filter(Boolean);
    if (!panels.length) return;

    const ctx = gsap.context(() => {
      panels.forEach((panel, i) => {
        const card = panel.querySelector(".stack-card");
        const targetScale = 1 - (panels.length - i) * 0.02;
        const topOffset = 100 + i * 12; // staggered "peek"

        ScrollTrigger.create({
          trigger: panel,
          start: `top ${topOffset}`,
          endTrigger: stackRef.current,
          end: "bottom bottom",
          pin: card,
          pinSpacing: false,
          anticipatePin: 1,
        });

        gsap.to(card, {
          scale: targetScale,
          transformOrigin: "top center",
          ease: "none",
          scrollTrigger: {
            trigger: panel,
            start: `top ${topOffset}`,
            endTrigger: stackRef.current,
            end: "bottom bottom",
            scrub: true,
          },
        });
      });

      ScrollTrigger.refresh();
    }, sectionRef);

    return () => ctx.revert();
  }, [isDesktop]);

  return (
    <section
      ref={sectionRef}
      className="projects-section"
      style={{
        background: "#0F0C1F",
        position: "relative",
        overflow: "hidden",
      }}
      id="projects"
    >
      <div className="projects-bg-decoration"></div>
      <Container className="position-relative" style={{ zIndex: 2 }}>
        {/* Section Header */}
        <div ref={headerRef} className="text-center pt-5 pb-4">
          <h2
            className="gradient-text1 text-blue-50"
            style={{ fontSize: "3rem", fontWeight: "bold", marginBottom: "1rem" }}
          >
            Current Projects
          </h2>
          <p
            style={{
              color: "#D1D5DB",
              maxWidth: "800px",
              margin: "0 auto",
              fontSize: "1.125rem",
            }}
          >
            Ready to embark on a journey of innovation and excellence? Join us as we dive
            into our exciting projects and discover limitless possibilities together.
          </p>
        </div>

        {isDesktop ? (
          /* Desktop: pinned stacking scroll */
          <div ref={stackRef} className="projects-stack">
            {projects.map((project, i) => (
              <div
                key={project.id}
                className="stack-panel"
                ref={(el) => (panelsRef.current[i] = el)}
              >
                <div className="stack-card">
                  <ProjectItem
                    project={project}
                    onClick={() => setSelectedProject(project)}
                  />
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* Mobile: carousel */
          <div ref={carouselRef} className="projects-carousel-wrapper">
            <Carousel
              showArrows={true}
              showStatus={false}
              showThumbs={false}
              infiniteLoop={true}
              autoPlay={true}
              interval={5000}
              transitionTime={800}
              swipeable={true}
              emulateTouch={true}
              className="custom-carousel"
            >
              {projects.map((project) => (
                <ProjectItem
                  key={project.id}
                  project={project}
                  onClick={() => setSelectedProject(project)}
                />
              ))}
            </Carousel>
          </div>
        )}
      </Container>

      {/* Project Detail Modal */}
      {selectedProject && (
        <div
          className="project-modal-overlay"
          onClick={() => setSelectedProject(null)}
        >
          <div
            className="project-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ position: "relative" }}>
              <img
                src={process.env.PUBLIC_URL + "/" + selectedProject.image}
                alt={selectedProject.title}
                style={{ width: "100%", height: "20rem", objectFit: "contain", objectPosition: "center", background: "rgba(255,255,255,0.03)" }}
              />
              <button
                onClick={() => setSelectedProject(null)}
                className="project-modal-close"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div style={{ padding: "1.5rem" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "0.75rem" }}>
                <span
                  style={{
                    padding: "0.25rem 0.75rem",
                    borderRadius: "50px",
                    fontSize: "0.75rem",
                    fontWeight: "600",
                    background: "rgba(105, 54, 244, 0.1)",
                    color: "#6936F4",
                  }}
                >
                  {selectedProject.category}
                </span>
                <span style={{ color: "#6B7280", fontSize: "0.875rem" }}>{selectedProject.year}</span>
              </div>
              <h3 style={{ fontSize: "1.5rem", fontWeight: "bold", marginBottom: "0.75rem", color: "#111827" }}>
                {selectedProject.title}
              </h3>
              <p style={{ color: "#4B5563", marginBottom: "1rem" }}>{selectedProject.description}</p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
                {selectedProject.tech.map((tech, idx) => (
                  <span
                    key={idx}
                    style={{
                      padding: "0.25rem 0.75rem",
                      background: "#F3F4F6",
                      borderRadius: "50px",
                      fontSize: "0.75rem",
                      color: "#374151",
                    }}
                  >
                    {tech}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default CurrentProjects;